import type { OrderItem } from '@app/_proto/Protos/orders';
import { PaymentMethodGateway, PaymentMethodItem, PaymentMethodType } from '@app/_proto/Protos/payments';
import { type ProductItem, ProductPlatform, PurchaseProductRequest } from '@app/_proto/Protos/products';
import { ProductsClient } from '@app/_proto/Protos/products.client';
import { StringValue } from '@app/_proto/google/protobuf/wrappers';
import { logCheckout, logPurchase } from '@app/analytics';
import PayPalPayments from '@app/components/braintree/paypal.payments';
import ButtonSpinner from '@app/components/button.spinner';
import KarmaProducts from '@app/components/karma/karma.products';
import KarmaPurchaseSelection from '@app/components/karma/karma.purchase.selection';
import PaymentsAddCard from '@app/components/payments/payments.add.card';
import PaymentsFailedDialog from '@app/components/payments/payments.failed.dialog';
import PaymentsSelection from '@app/components/payments/payments.selection';
import ProductSuccess from '@app/components/products/product.success';
import ProductsCompletedDialog from '@app/components/products/products.completed.dialog';
import { useHttp } from '@app/libs/http';
import { batch, decimalToNumber } from '@app/libs/utils';
import { getStripe } from '@app/utils/stripe';
import { Close } from '@mui/icons-material';
import { Dialog, DialogContent, DialogTitle, Divider } from '@mui/material';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';

export type KarmaPurchaseDialogProps = {
    open: boolean;
    karmaProduct?: ProductItem;
    onClose?: () => void;
    onSuccess?: () => void;
};

export default function KarmaPurchaseDialog({ open, karmaProduct, onSuccess, onClose }: KarmaPurchaseDialogProps) {
    const { grpcRequest } = useHttp();

    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodItem | null>(null);

    const [error, setError] = useState<string | null>(null);
    const [showError, setShowError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<ProductItem | null>(karmaProduct || null);
    const [needsPayPal, setNeedsPayPal] = useState(false);
    const [needsAddCard, setNeedsAddCard] = useState(false);
    const [order, setOrder] = useState<OrderItem | null>(null);
    const [completed, setCompleted] = useState(false);
    const [waitingForActivation, setWaitingForActivation] = useState(false);

    const handleOrderCompleted = useCallback(
        (order: OrderItem) => {
            const product = selectedProduct ?? karmaProduct;

            if (product) {
                logPurchase({
                    price: decimalToNumber(product.price),
                    transactionId: order.id.toString(),
                    productId: product.id,
                    items: [
                        {
                            id: product.id,
                            name: product.name,
                            category: 'karma',
                            price: decimalToNumber(product.price),
                            quantity: product.quantity,
                        },
                    ],
                });
            }

            batch(() => {
                setWaitingForActivation(false);
                setOrder(order);
                setCompleted(true);
            });
        },
        [selectedProduct]
    );

    const handleError = useCallback((error?: string | null) => {
        batch(() => {
            setError(error ? error : null);
            setShowError(error ? true : false);
        });
    }, []);

    const doPurchase = useCallback(
        async (product: ProductItem, paymentMethod: PaymentMethodItem | string | null, type?: PaymentMethodType) => {
            logCheckout(decimalToNumber(product.price), [
                {
                    id: product.id,
                    name: product.name,
                    category: 'karma',
                    quantity: product.quantity,
                    price: decimalToNumber(product.price),
                },
            ]);

            if (!paymentMethod || !product) {
                return;
            }

            setLoading(true);

            if (PaymentMethodItem.is(paymentMethod) && paymentMethod?.id?.value === 'PAYPAL_EMPTY') {
                batch(() => {
                    setSelectedProduct(product);
                    setNeedsPayPal(true);
                });

                return;
            }

            if (PaymentMethodItem.is(paymentMethod) && paymentMethod?.id?.value === 'CARD_EMPTY') {
                batch(() => {
                    setSelectedProduct(product);
                    setNeedsAddCard(true);
                });

                return;
            }

            const request = PurchaseProductRequest.create({
                productId: product.id,
                confirmPrice: product.price,
                platform: ProductPlatform.WebProduct,
            });

            let gateway: PaymentMethodGateway | null = null;

            if (PaymentMethodItem.is(paymentMethod)) {
                gateway = paymentMethod.gateway;

                PurchaseProductRequest.mergePartial(request, {
                    paymentGateway: gateway,
                    paymentMethodType: paymentMethod.type,
                    paymentMethod: {
                        oneofKind: 'paymentMethodId',
                        paymentMethodId: paymentMethod.id!,
                    },
                });
            } else if (typeof paymentMethod === 'string' && type === PaymentMethodType.PayPalType) {
                gateway = PaymentMethodGateway.Braintree;

                PurchaseProductRequest.mergePartial(request, {
                    paymentGateway: gateway,
                    paymentMethodType: type,
                    paymentMethod: {
                        oneofKind: 'paymentToken',
                        paymentToken: StringValue.create({
                            value: paymentMethod,
                        }),
                    },
                });
            }

            try {
                const { token, order } = await grpcRequest(ProductsClient, c => c.purchaseProduct, request);

                if (
                    PaymentMethodItem.is(paymentMethod) &&
                    paymentMethod.gateway == PaymentMethodGateway.Stripe &&
                    token?.value
                ) {
                    const stripe = await getStripe();

                    const stripeResult = await stripe!.confirmCardPayment(token.value, {
                        payment_method: paymentMethod.id?.value!,
                    });

                    if (stripeResult.error) {
                        console.error(stripeResult.error);
                        handleError(stripeResult.error.message);
                    } else if (stripeResult.paymentIntent?.status === 'succeeded') {
                        batch(() => {
                            setOrder(order || null);
                            setWaitingForActivation(true);
                        });

                        onClose?.();
                    }
                } else {
                    if (order?.completed) {
                        handleOrderCompleted(order);
                    } else {
                        batch(() => {
                            setOrder(order || null);
                            setWaitingForActivation(true);
                        });

                        onClose?.();
                    }
                }
            } catch (e) {
                handleError(e.response?.description);
            } finally {
                setLoading(false);
            }
        },
        [grpcRequest, handleError, handleOrderCompleted, onClose]
    );

    const handleChange = useCallback((paymentMethod: PaymentMethodItem | null) => {
        setPaymentMethod(paymentMethod);
    }, []);

    const handlePurchase = useCallback(
        (product: ProductItem) => {
            doPurchase(product, paymentMethod);
        },
        [doPurchase, paymentMethod]
    );

    const handleClickBuy = useCallback(() => {
        if (selectedProduct) {
            doPurchase(selectedProduct, paymentMethod);
        }
    }, [doPurchase, paymentMethod, selectedProduct]);

    const handleSelectPayPal = useCallback(
        (paymentMethod: PaymentMethodItem | string) => {
            if (PaymentMethodItem.is(paymentMethod)) {
                batch(() => {
                    setPaymentMethod(paymentMethod);
                    setLoading(false);
                    setNeedsPayPal(false);
                });

                return;
            }

            setNeedsPayPal(false);

            if (selectedProduct) {
                doPurchase(selectedProduct, paymentMethod, PaymentMethodType.PayPalType);
            }
        },
        [doPurchase, selectedProduct]
    );

    const handleErrorPayPal = useCallback(
        (errorMessage: string) => {
            batch(() => {
                setLoading(false);
                setNeedsPayPal(false);
                handleError(errorMessage);
            });
        },
        [handleError]
    );

    const handleAddCardActionCompleted = useCallback(
        async (paymentMethod: PaymentMethodItem | null) => {
            if (!loading || !paymentMethod) {
                setLoading(false);
                setNeedsAddCard(false);

                return;
            }

            setNeedsAddCard(false);

            if (selectedProduct) {
                doPurchase(selectedProduct, paymentMethod, PaymentMethodType.CardType);
            }
        },
        [doPurchase, loading, selectedProduct]
    );

    const handleCloseAddCard = useCallback(() => {
        batch(() => {
            setLoading(false);
            setNeedsAddCard(false);
        });
    }, []);

    const handleCompleted = useCallback(() => {
        setCompleted(false);

        onSuccess?.();
    }, [onSuccess]);

    useEffect(() => {
        if (karmaProduct) {
            setSelectedProduct(karmaProduct);
        }
    }, [karmaProduct]);

    return (
        <>
            <Dialog
                fullWidth
                open={open}
                onClose={onClose}
                PaperProps={{
                    className: 'dark:!bg-[#202020]',
                    style: {
                        maxWidth: '480px',
                    },
                }}
            >
                <DialogTitle className="flex items-center justify-between pl-[20px] pr-[11px] pt-[20px]">
                    <span className="text-[18px] font-bold">Get more Karma</span>
                    <Close onClick={onClose} className="cursor-pointer text-[#777]" />
                </DialogTitle>
                <DialogContent className="p-[20px]">
                    <div
                        className={clsx('mb-[20px] flex flex-col space-y-[20px]', {
                            'pointer-events-none': loading,
                        })}
                    >
                        {!!karmaProduct && (
                            <>
                                <KarmaPurchaseSelection productSelection={karmaProduct} />
                                <Divider />
                            </>
                        )}
                        <PaymentsSelection mode="purchase" onChange={handleChange} />
                        {!karmaProduct && (
                            <KarmaProducts
                                paymentMethod={paymentMethod}
                                loading={loading}
                                onPurchase={handlePurchase}
                            />
                        )}
                    </div>
                    {!!karmaProduct && (
                        <div className="flex items-center justify-end">
                            <ButtonSpinner
                                spinner={loading}
                                disabled={loading}
                                onClick={handleClickBuy}
                                className="w-[124px] !text-[13px] !font-semibold"
                            >
                                BUY
                            </ButtonSpinner>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <PayPalPayments
                enabled={needsPayPal}
                mode="once"
                amount={decimalToNumber(selectedProduct?.price)}
                onSelect={handleSelectPayPal}
                onError={handleErrorPayPal}
            />
            <PaymentsAddCard
                open={needsAddCard}
                onClose={handleCloseAddCard}
                onCompleted={handleAddCardActionCompleted}
            />
            <ProductSuccess enabled={waitingForActivation} order={order} onOrderCompleted={handleOrderCompleted} />
            <ProductsCompletedDialog open={completed} onDismiss={handleCompleted} />
            <PaymentsFailedDialog open={showError} error={error} onDismiss={() => handleError(null)} />
        </>
    );
}
